
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, ArrowLeft, Loader2, Star, StarOff, Tag, Trash2 } from 'lucide-react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { promptHistoryService, PromptHistory } from '@/services/promptHistoryService';
import { useToast } from '@/hooks/use-toast';

const History = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const [prompts, setPrompts] = useState<PromptHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptHistory | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!currentUser) {
      navigate('/?showAuth=true');
      return;
    }
    loadPrompts();
  }, [currentUser]);

  const loadPrompts = async () => {
    try {
      const history = await promptHistoryService.getPromptHistory(currentUser!.uid);
      setPrompts(history);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load prompt history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (promptId: string, favorite: boolean) => {
    try {
      await promptHistoryService.toggleFavorite(currentUser!.uid, promptId, !favorite);
      setPrompts(prompts.map(p => 
        p.id === promptId ? { ...p, favorite: !favorite } : p
      ));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update favorite status",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (promptId: string) => {
    try {
      await promptHistoryService.deletePrompt(currentUser!.uid, promptId);
      setPrompts(prompts.filter(p => p.id !== promptId));
      toast({
        title: "Success",
        description: "Prompt deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete prompt",
        variant: "destructive",
      });
    }
  };

  const handlePromptClick = (prompt: PromptHistory) => {
    setSelectedPrompt(prompt);
    // Store the description and navigate to results
    sessionStorage.setItem('projectDescription', prompt.description);
    navigate('/results', { state: { promptHistory: prompt } });
  };

  // If user is not authenticated and not currently loading, redirect to home
  if (!authLoading && !currentUser) {
    return <Navigate to="/" />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-950">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Fixed background elements */}
      <div className="fixed inset-0 z-[-1] overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-background to-background/90"></div>
        <div className="absolute top-0 -left-40 w-96 h-96 bg-purple-600/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-indigo-600/10 rounded-full filter blur-3xl"></div>
      </div>
      
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Generator
          </Link>
          
          <h1 className="text-3xl md:text-4xl font-bold mt-4 mb-2">Your Prompt History</h1>
          <p className="text-muted-foreground">
            View and reuse all your previously generated prompts
          </p>
        </div>
        
        <div className="grid gap-6">
          {prompts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p>No prompts generated yet.</p>
              <Button 
                className="mt-4"
                onClick={() => navigate('/')}
              >
                Generate Your First Prompt
              </Button>
            </div>
          ) : (
            prompts.map((prompt) => (
              <div 
                key={prompt.id} 
                className="bg-gray-800/50 rounded-lg p-6 border border-white/10 hover:border-purple-500/30 transition-all cursor-pointer"
                onClick={() => handlePromptClick(prompt)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {prompt.description.slice(0, 100)}...
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDistanceToNow(prompt.timestamp instanceof Date ? prompt.timestamp : new Date(prompt.timestamp), { addSuffix: true })}
                      </div>
                      <div className="flex items-center gap-1">
                        <Tag className="h-4 w-4" />
                        {prompt.tokensUsed} tokens used
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(prompt.id!, prompt.favorite);
                      }}
                    >
                      {prompt.favorite ? (
                        <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      ) : (
                        <StarOff className="h-5 w-5 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(prompt.id!);
                      }}
                    >
                      <Trash2 className="h-5 w-5 text-red-400" />
                    </Button>
                  </div>
                </div>
                
                {prompt.tags && prompt.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {prompt.tags.map((tag) => (
                      <span 
                        key={tag}
                        className="px-2 py-1 text-xs rounded-full bg-purple-500/20 text-purple-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default History;
